import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useT } from '../i18n';

// S10-T07 (US-S10-007): tour interativo do onboarding assistido.
//
// Implementacao propria (sem dependencia externa — o bundle do frontend e
// deliberadamente minimo). Cada passo pode apontar para um elemento da
// interface via atributo ``data-tour="<target>"`` (os itens de navegacao do
// Layout expoem ``data-tour="nav-<view>"``). Quando o alvo existe, o tour
// desenha um spotlight sobre o elemento e posiciona o tooltip ao lado;
// quando nao existe (item oculto por role/plano), o passo cai para um card
// centralizado — o tour nunca quebra por causa de um alvo ausente.

export interface TourStepDef {
  key: string;
  /** Valor do atributo data-tour do elemento a destacar (opcional). */
  target?: string;
  title: string;
  body: string;
}

interface OnboardingTourProps {
  steps: TourStepDef[];
  /** Chamado quando o usuario conclui o ultimo passo ("Concluir"). */
  onFinish: () => void;
  /** Chamado quando o usuario fecha o tour sem concluir ("Pular"). */
  onSkip: () => void;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const CARD_W = 320;
const CARD_H = 200;
const PAD = 8;

function findTarget(target?: string): Rect | null {
  if (!target) return null;
  // O mesmo data-tour existe na nav desktop e na mobile — pega o primeiro
  // elemento VISIVEL (o oculto tem rect zerado). Nenhum visivel -> null e
  // o passo cai no card centralizado.
  for (const el of document.querySelectorAll(`[data-tour="${target}"]`)) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) {
      return { top: r.top, left: r.left, width: r.width, height: r.height };
    }
  }
  return null;
}

function cardPosition(rect: Rect | null): { top: number; left: number } {
  if (!rect) {
    return {
      top: Math.max(16, window.innerHeight / 2 - CARD_H / 2),
      left: Math.max(16, window.innerWidth / 2 - CARD_W / 2),
    };
  }
  // Preferencia: abaixo do alvo; se nao couber, acima; clamp na viewport.
  let top = rect.top + rect.height + PAD + 8;
  if (top + CARD_H > window.innerHeight - 16) {
    top = rect.top - CARD_H - PAD - 8;
  }
  top = Math.min(Math.max(16, top), Math.max(16, window.innerHeight - CARD_H - 16));
  let left = rect.left + rect.width / 2 - CARD_W / 2;
  left = Math.min(Math.max(16, left), Math.max(16, window.innerWidth - CARD_W - 16));
  return { top, left };
}

function OnboardingTour({ steps, onFinish, onSkip }: OnboardingTourProps) {
  const t = useT();
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  const step = steps[idx];
  const last = idx === steps.length - 1;

  const measure = useCallback(() => {
    setRect(findTarget(step?.target));
  }, [step]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  // Recalcula o spotlight em resize/scroll (layout muda, alvo se move).
  useEffect(() => {
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  // Esc fecha o tour (sem concluir — mesmo efeito de "Pular").
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSkip();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSkip]);

  const pos = cardPosition(rect);

  if (!step) return null;

  return (
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true">
      {/* Backdrop com "buraco" (spotlight) sobre o alvo quando existir. */}
      {rect ? (
        <div
          className="pointer-events-none absolute rounded-md ring-2 ring-brand-400"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-black/55" />
      )}

      {/* Card do passo */}
      <div
        className="absolute w-80 rounded-lg border border-brand-200 bg-white p-4 shadow-xl"
        style={{ top: pos.top, left: pos.left }}
      >
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-brand-900">{step.title}</h3>
          <span className="text-xs text-brand-400">
            {t('onboarding.tour.counter', { current: idx + 1, total: steps.length })}
          </span>
        </div>
        <p className="mb-4 text-sm text-brand-600">{step.body}</p>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onSkip}
            className="text-xs font-medium text-brand-500 hover:text-brand-700"
          >
            {t('onboarding.tour.skip')}
          </button>
          <div className="flex gap-2">
            {idx > 0 && (
              <button
                type="button"
                onClick={() => setIdx((i) => i - 1)}
                className="rounded-md border border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50"
              >
                {t('onboarding.tour.prev')}
              </button>
            )}
            <button
              type="button"
              onClick={() => (last ? onFinish() : setIdx((i) => i + 1))}
              className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800"
            >
              {last ? t('onboarding.tour.finish') : t('onboarding.tour.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnboardingTour;
